class CartDrawerRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartDrawer = this.closest('cart-drawer');
      cartDrawer.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-drawer-remove-button', CartDrawerRemoveButton);

class JhCartDiscount extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.applyButton = this.querySelector('.jh-cart-drawer__discount-form button');
    this.removeButtons = this.querySelectorAll('[data-remove-discount]');
    this.status = this.querySelector('[role="status"]');

    if (this.input && this.applyButton) {
      this.applyButton.addEventListener('click', () => this.apply(this.input.value));
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.apply(this.input.value);
        }
      });
    }

    this.removeButtons.forEach((button) => {
      button.addEventListener('click', () => this.apply('', true));
    });
  }

  apply(value, isRemoval = false) {
    const code = String(value || '').trim();
    if (!isRemoval && !code) {
      this.setStatus('Enter a discount code.', 'error');
      this.input.focus();
      return;
    }

    this.setLoading(true);
    this.setStatus(isRemoval ? 'Removing…' : 'Applying…');

    const body = JSON.stringify({
      discount: code,
      sections: [this.dataset.sectionId],
      sections_url: window.location.pathname
    });

    fetch(routes.cart_update_url, { ...fetchConfig(), body })
      .then((response) => {
        if (!response.ok) throw new Error('Discount update failed');
        return response.json();
      })
      .then((cart) => {
        const applications = cart.cart_level_discount_applications || [];
        const discountCodes = cart.discount_codes || [];
        const applied =
          applications.some((application) => {
            return String(application.title || '').trim().toLowerCase() === code.toLowerCase();
          }) ||
          discountCodes.some((discount) => {
            return String(discount.code || '').trim().toLowerCase() === code.toLowerCase() &&
              discount.applicable !== false;
          });

        if (!isRemoval && !applied) {
          this.setStatus('This code is invalid or is not eligible for the items in your cart.', 'error');
          return;
        }

        const drawer = this.closest('cart-drawer');
        if (drawer && cart.sections && cart.sections[this.dataset.sectionId]) {
          drawer.renderContents(cart, false, '', '', true);
        }
      })
      .catch(() => {
        this.setStatus('Unable to update the discount. Please try again.', 'error');
      })
      .finally(() => this.setLoading(false));
  }

  setLoading(isLoading) {
    if (this.applyButton) this.applyButton.disabled = isLoading;
    this.removeButtons.forEach((button) => { button.disabled = isLoading; });
  }

  setStatus(message, state = '') {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.dataset.state = state;
  }
}

if (!customElements.get('jh-cart-discount')) {
  customElements.define('jh-cart-discount', JhCartDiscount);
}

class JhCartNote extends HTMLElement {
  constructor() {
    super();
    this.toggle = this.querySelector('input[type="checkbox"]');
    this.field = this.querySelector('.jh-cart-drawer__note-field');
    this.textarea = this.querySelector('textarea');
    this.counter = this.querySelector('[data-note-count]');

    if (!this.toggle || !this.field || !this.textarea) return;

    this.toggle.addEventListener('change', () => {
      this.field.hidden = !this.toggle.checked;

      if (this.toggle.checked) {
        window.setTimeout(() => {
          this.textarea.focus({ preventScroll: true });
          const end = this.textarea.value.length;
          this.textarea.setSelectionRange(end, end);
        }, 80);
      }
    });

    this.save = debounce(() => this.updateNote(), 500);
    this.textarea.addEventListener('input', () => {
      if (this.counter) this.counter.textContent = this.textarea.value.length;
      this.save();
    });
  }

  updateNote() {
    fetch(routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify({ note: this.textarea.value })
    })
      .then((response) => {
        if (!response.ok) throw new Error('Cart note update failed');
      })
      .catch((error) => window.console.error(error));
  }
}

if (!customElements.get('jh-cart-note')) {
  customElements.define('jh-cart-note', JhCartNote);
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawer = document.getElementById('cart-drawer');
    this.cartDrawerOpen = document.querySelector('[data-js-cart-drawer="cartDrawerOpen"]');
    this.cartDrawerClose = document.querySelector('[data-js-cart-drawer="cartDrawerClose"]');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.syncVisualViewport = this.handleVisualViewport.bind(this);
    this.onDrawerFocusIn = this.handleDrawerFocusIn.bind(this);
    this.itemCountHolder = document.querySelector('[data-js-cart-item-count]');

    this.lineItemStatusElement = document.getElementById('cart-drawer-line-item-status');

    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener('change', this.debouncedOnChange.bind(this));
    this.addEventListener('click', (event) => {
      const discountRemove = event.target.closest('[data-remove-discount]');
      if (!discountRemove) return;

      event.preventDefault();
      const discountControl = this.querySelector('jh-cart-discount');
      if (discountControl) discountControl.apply('', true);
    });

    this.drawer.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.drawer.addEventListener('focusin', this.onDrawerFocusIn);
    this.querySelectorAll('[data-js-cart-drawer="cartDrawerClose"]').forEach((closeButton) => {
      this.activeElement = document.activeElement || this.cartDrawerOpen;
      closeButton.addEventListener('click', this.close.bind(this));
    });

    document.querySelectorAll('[data-js-cart-drawer="cartDrawerOpen"]').forEach((openButton) => {
      openButton.addEventListener('click', this.handleDrawerOpen.bind(this))
    });
  }

  onChange(event) {
    const quantityInput = event.target.closest('input[name="updates[]"][data-index]');
    if (!quantityInput) return;

    this.updateQuantity(
      quantityInput.dataset.index,
      quantityInput.value,
      quantityInput.getAttribute('name')
    );
  }

  /**
   * Open the cart-drawer
   */
  handleDrawerOpen() {
    if (theme.pageType === 'cart') {
      return;
    }
    this.open();
  }

  /**
   * Triggered whenever product-form was submitted and item was added to cart
   * @param  {Object} Added to cart item data
   */
  handleAddedItem(addedItemResponse) {
    const fetchedCartData = this.getCart();
    this.handleCartData(fetchedCartData, addedItemResponse);
  }

  /**
   * Get Cart data from Shopify Ajax API
   * @return {Object} Promise
   */
  getCart() {
    return new Promise((resolve, reject) => {
      fetch(`${routes.ajax_api}`)
        .then((response) => response.json())
        .then((response) => {
          if (response) {
            resolve(response);
            return;
          }

          reject(response);
        })
        .catch((error) => {
          window.console.error(error);
        });
    });
  }

  /**
   * Handle cart data after the response from Shopify Ajax API
   * @param  {Object} Promise
   * @param  {Object} Added to cart item data
   */
  handleCartData(promise, addedItemResponse = false) {
    Promise.all([promise]).then((values) => {
      if (values.length === 0) {
        return;
      }

      const cartData = values[0];

      if (addedItemResponse !== false) {
        const line = cartData.items.map((item, index) => {
          if (item.id === addedItemResponse.id) {
            return index;
          }
        });

        this.renderContents(cartData, addedItemResponse, line, '', false);
      }
    })
    .catch((error) => {
      window.console.error(error);
    });
  }

  /**
   * Get sections to render
   * @return {Object}
   */
  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: document.getElementById('cart-drawer').dataset.id,
        selector: '.js-contents',
      }
    ];
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, {...fetchConfig(), ...{ body }})
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        this.renderContents(parsedState, false, line, name, true);

        if (quantity == 0) {
          this.updateTrappedFocus();
        }
      }).catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        document.getElementById('cart-drawer-errors').textContent = window.cartStrings.error;
        this.disableLoading();
      });
  }

  open() {
    this.activeElement = document.activeElement || this.cartDrawerOpen;
    this.drawer.classList.add('animate', 'active');

    if (window.bodyScrollLock) {
      bodyScrollLock.disableBodyScroll(this.drawer, {
        reserveScrollBarGap: true,
        allowTouchMove: (target) => Boolean(target.closest('.cart-drawer__contents'))
      });
    }

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.syncVisualViewport);
      window.visualViewport.addEventListener('scroll', this.syncVisualViewport);
    }
    this.handleVisualViewport();

    this.drawer.addEventListener('transitionend', () => {
      this.drawer.focus();
      trapFocus(this.drawer, this.cartDrawerClose);
    }, { once: true });

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.drawer.classList.remove('active');

    if (window.bodyScrollLock) {
      bodyScrollLock.enableBodyScroll(this.drawer);
    }

    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.syncVisualViewport);
      window.visualViewport.removeEventListener('scroll', this.syncVisualViewport);
    }
    this.drawer.classList.remove('cart-drawer--keyboard-open');
    this.drawer.style.removeProperty('height');
    this.drawer.style.removeProperty('top');

    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  handleDrawerFocusIn(event) {
    if (!event.target.matches('input, textarea, select')) return;

    window.setTimeout(() => {
      this.handleVisualViewport();
      this.keepFocusedControlVisible(event.target);
    }, 320);
  }

  handleVisualViewport() {
    if (!this.drawer.classList.contains('active')) return;

    const viewport = window.visualViewport;
    const viewportHeight = viewport ? viewport.height : window.innerHeight;
    const viewportTop = viewport ? viewport.offsetTop : 0;
    const layoutHeight = document.documentElement.clientHeight || window.innerHeight;
    const keyboardOpen = layoutHeight - viewportHeight > 120;
    const keyboardWasOpen = this.drawer.classList.contains('cart-drawer--keyboard-open');

    this.drawer.style.height = `${Math.round(viewportHeight)}px`;
    this.drawer.style.top = `${Math.round(viewportTop)}px`;
    this.drawer.classList.toggle('cart-drawer--keyboard-open', keyboardOpen);

    if (keyboardWasOpen && !keyboardOpen) {
      const scrollContainer = this.drawer.querySelector('.cart-drawer__contents');
      if (scrollContainer) scrollContainer.scrollTop = 0;
    }

    if (keyboardOpen && this.drawer.contains(document.activeElement)) {
      window.requestAnimationFrame(() => this.keepFocusedControlVisible(document.activeElement));
    }
  }

  keepFocusedControlVisible(control) {
    if (!control || !this.drawer.contains(control)) return;

    const scrollContainer = this.drawer.querySelector('.cart-drawer__contents');
    if (!scrollContainer) return;

    const controlRect = control.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();
    const safeTop = containerRect.top + 16;
    const safeBottom = containerRect.bottom - 16;

    if (controlRect.bottom > safeBottom) {
      scrollContainer.scrollTop += controlRect.bottom - safeBottom;
    } else if (controlRect.top < safeTop) {
      scrollContainer.scrollTop -= safeTop - controlRect.top;
    }
  }

  /**
   * Render Contents
   * @param  {Object} Cart data
   * @param  {Object/Boolean} Added to cart item data / False
   * @param  {Number}  Line item index
   * @param  {String}  Line item input name
   * @param  {Boolean} Whether Cart-drawer is opened
   */
  renderContents(parsedState, addedItemResponse = false, line, name, isDrawerOpened = false) {
    const drawerInner = this.drawer.querySelector('.cart-drawer__inner');
    const drawerScrollTop = drawerInner ? drawerInner.scrollTop : 0;

    this.classList.toggle('is-empty', parsedState.item_count === 0);
    this.itemCountHolder.innerText = parsedState.item_count;
    this.itemCountHolder.classList.toggle('is-active', parsedState.item_count > 0);
    const cartFooter = document.getElementById('cart-drawer-footer');

    if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);

    this.getSectionsToRender().forEach((section => {
      let state = parsedState;

      if (addedItemResponse !== false) {
        state = addedItemResponse;
      }

      const elementToReplace =
        document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

      elementToReplace.innerHTML =
        this.getSectionInnerHTML(state.sections[section.section], section.selector);
    }));

    const updatedDrawerInner = this.drawer.querySelector('.cart-drawer__inner');
    if (updatedDrawerInner) updatedDrawerInner.scrollTop = drawerScrollTop;

    this.updateLiveRegions(line, parsedState.item_count);
    const lineItem =  document.getElementById(`CartDrawerItem-${line}`);
    if (lineItem && lineItem.querySelector(`[name="${name}"]`)) lineItem.querySelector(`[name="${name}"]`).focus();
    this.disableLoading();

    if (!isDrawerOpened) {
      if (this.header) this.header.reveal();
      this.open();
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  updateLiveRegions(line, itemCount) {
    if (line == '' || !line) {
      return;
    }

    if (this.currentItemCount === itemCount) {
      document.getElementById(`CartDrawerItem-error-${line}`)
        .querySelector('.cart-item__error-text')
        .innerHTML = window.cartStrings.quantityError.replace(
          '[quantity]',
          document.getElementById(`CartDrawerItem-quantity-${line}`).value
        );
    }

    this.currentItemCount = itemCount;
    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-drawer-live-region-text');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  enableLoading(line) {
    document.getElementById('cart-drawer').classList.add('cart-drawer--disabled');
    this.querySelectorAll(`#CartDrawerItem-${line} .loading-overlay`).forEach((overlay) => overlay.classList.remove('hidden'));
    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    document.getElementById('cart-drawer').classList.remove('cart-drawer--disabled');
  }

  handleBodyClick(event) {
    const target = event.target;
    const isNotDrawer = (target !== this.drawer) && (!target.closest('cart-drawer'));
    const isNotButtonOpen = target !== this.cartDrawerOpen && !target.closest('[data-js-cart-drawer="cartDrawerOpen"]');
    const isCloseButton = (target === this.cartDrawerClose) || (target.closest('[data-js-cart-drawer="cartDrawerClose"]'));

    if (isNotDrawer && isNotButtonOpen || isCloseButton) {
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  updateTrappedFocus() {
    removeTrapFocus(document.activeElement);

    setTimeout(() => {
      this.setActiveElement(this.cartDrawerClose || this.cartDrawerOpen);
      this.drawer.focus();
      trapFocus(this.drawer, this.cartDrawerClose);
    });
  }
}

customElements.define('cart-drawer', CartDrawer);
